import React, { useState } from 'react'

import {
  getMax,
  getMaxHourFromRangeAsNumber,
  getMin,
  getMinHourFromRangeAsNumber,
  getRangeAsStringValues,
  HourOfDaySelect,
} from 'cube/components/HourRangeSelect'
import { isNil } from 'ramda'

import { Flex, Text } from '@driftt/dds'

interface HourRangeSelectProps {
  hourOfDayRange: string[]
  isDisabled: boolean
  onChange: (hours: string[]) => void
}

/**
 *
 * Manages the time of day range between a min (inclusive) and max (exclusive). Returns a range of hours represented as strings.
 * For ex, selecting 9AM - 11AM will return ['9', '10'].
 *
 * @param {HourRangeSelectProps} HourRangeSelectProps
 * @returns {JSX.Element} JSX.Element
 */
export const HourRangeSelect = ({
  hourOfDayRange,
  isDisabled,
  onChange,
}: HourRangeSelectProps) => {
  const [hourRange, setHourRange] = useState<{
    min: number | null
    max: number | null
  }>({
    min: getMinHourFromRangeAsNumber(hourOfDayRange),
    max: getMaxHourFromRangeAsNumber(hourOfDayRange),
  })

  const handleUpdateRange = (min: number | null, max: number | null) => {
    const minValue = getMin(min, max)
    const maxValue = getMax(min, max)
    setHourRange({
      min: minValue,
      max: maxValue,
    })
    if (!isNil(minValue) && !isNil(maxValue)) {
      onChange(getRangeAsStringValues(minValue as number, maxValue as number))
    }
  }

  return (
    <Flex alignItems="baseline">
      <HourOfDaySelect
        hourOfDay={hourRange.min}
        isDisabled={isDisabled}
        onChange={(value) => handleUpdateRange(value, hourRange.max)}
      />
      <Text variant="body">to</Text>
      <HourOfDaySelect
        hourOfDay={hourRange.max}
        isDisabled={isDisabled}
        onChange={(value) => handleUpdateRange(hourRange.min, value)}
      />
    </Flex>
  )
}
